import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = {
  od_sfera: "", od_cilindro: "", od_asse: "", od_add: "",
  os_sfera: "", os_cilindro: "", os_asse: "", os_add: "",
  tipo_lente: "", note: "", data_controllo: "",
  data_ultimo_controllo: "", data_ultimo_acquisto: "",
};

export default function PrescrizioneForm({ open, onOpenChange, clienteId, onSaved, prescrizione }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(prescrizione && prescrizione.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      if (prescrizione) {
        setForm({
          ...EMPTY,
          od_sfera: prescrizione.od_sfera || "", od_cilindro: prescrizione.od_cilindro || "", od_asse: prescrizione.od_asse || "", od_add: prescrizione.od_add || "",
          os_sfera: prescrizione.os_sfera || "", os_cilindro: prescrizione.os_cilindro || "", os_asse: prescrizione.os_asse || "", os_add: prescrizione.os_add || "",
          tipo_lente: prescrizione.tipo_lente || "", note: prescrizione.note || "", data_controllo: prescrizione.data_controllo || "",
        });
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setForm({ ...EMPTY, data_controllo: today, data_ultimo_controllo: today });
      }
    }
  }, [open, prescrizione]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { cliente_id: clienteId, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.cliente_id = clienteId;
      if (isEdit) {
        await api.put(`/prescrizioni/${prescrizione.id}`, payload);
        toast.success("Prescrizione aggiornata");
      } else {
        await api.post("/prescrizioni", payload);
        toast.success("Prescrizione registrata");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const eye = (prefix, label) => (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1"><Label className="text-xs">Sfera</Label><Input data-testid={`${prefix}-sfera`} value={form[`${prefix}_sfera`]} onChange={(e) => set(`${prefix}_sfera`, e.target.value)} placeholder="+1.25" className="font-mono" /></div>
        <div className="space-y-1"><Label className="text-xs">Cilindro</Label><Input data-testid={`${prefix}-cilindro`} value={form[`${prefix}_cilindro`]} onChange={(e) => set(`${prefix}_cilindro`, e.target.value)} placeholder="-0.75" className="font-mono" /></div>
        <div className="space-y-1"><Label className="text-xs">Asse</Label><Input data-testid={`${prefix}-asse`} value={form[`${prefix}_asse`]} onChange={(e) => set(`${prefix}_asse`, e.target.value)} placeholder="90°" className="font-mono" /></div>
        <div className="space-y-1"><Label className="text-xs">ADD</Label><Input data-testid={`${prefix}-add`} value={form[`${prefix}_add`]} onChange={(e) => set(`${prefix}_add`, e.target.value)} placeholder="+2.00" className="font-mono" /></div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto custom-scroll" data-testid="prescrizione-form">
        <DialogHeader><DialogTitle className="font-head">{isEdit ? "Modifica prescrizione ottica" : "Nuova prescrizione ottica"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {eye("od", "OD — Occhio Destro")}
          {eye("os", "OS — Occhio Sinistro")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo lente</Label>
              <Select value={form.tipo_lente} onValueChange={(v) => set("tipo_lente", v)}>
                <SelectTrigger data-testid="presc-tipo-lente"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monofocale">Monofocale</SelectItem>
                  <SelectItem value="Progressiva">Progressiva</SelectItem>
                  <SelectItem value="Bifocale">Bifocale</SelectItem>
                  <SelectItem value="A contatto">A contatto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data controllo</Label><Input type="date" data-testid="presc-data-controllo" value={form.data_controllo} onChange={(e) => set("data_controllo", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Aggiorna ultimo controllo</Label><Input type="date" data-testid="presc-ultimo-controllo" value={form.data_ultimo_controllo} onChange={(e) => set("data_ultimo_controllo", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Aggiorna ultimo acquisto</Label><Input type="date" data-testid="presc-ultimo-acquisto" value={form.data_ultimo_acquisto} onChange={(e) => set("data_ultimo_acquisto", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea data-testid="presc-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="save-prescrizione-btn" onClick={submit} disabled={saving} className="bg-[#1E40AF] hover:bg-[#1D4ED8]">{saving ? "Salvataggio…" : (isEdit ? "Salva modifiche" : "Registra")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
